import {
  CanvasTexture,
  Color,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  ShaderMaterial,
  Object3D,
} from 'three'
import * as TWEEN from '@tweenjs/tween.js'
import { animateChangeMaterials, TColorOption } from './animate-change-materials'
import { animateChangeMeshes, TOpacityOption } from './animate-change-meshes'
import { DiamondCut } from '../../../../trillion-common/src/objects/diamond-cut.object'
import { MaterialColor } from '../../../../trillion-common/src/objects/material-color.object'
import { ANIMATION_DURATION } from '../../viewerSettings'
import { StoneHalo } from '../../../../trillion-common/src/objects/stone-halo.object'
import { DiamondBand } from '../../../../trillion-common/src/objects/diamond-band.object'
import { DiamondBandDto } from '../../../../trillion-common/src/dto/diamond-band.dto'

type Material = MeshBasicMaterial | MeshStandardMaterial

const tweenMaterialsAnimations: Map<string, TWEEN.Tween<TColorOption>> = new Map()
const tweenCutAnimations = {
  tweenIn: null as TWEEN.Tween<TOpacityOption> | null,
  tweenOut: null as TWEEN.Tween<TOpacityOption> | null,
}
const tweenLettersAnimations = {
  tweenIn: null as TWEEN.Tween<TOpacityOption> | null,
  tweenOut: null as TWEEN.Tween<TOpacityOption> | null,
}
const tweenHalosAnimations = {
  tweenIn: null as TWEEN.Tween<TOpacityOption> | null,
  tweenOut: null as TWEEN.Tween<TOpacityOption> | null,
}
const tweenBandAnimations = {
  tweenIn: null as TWEEN.Tween<TOpacityOption> | null,
  tweenOut: null as TWEEN.Tween<TOpacityOption> | null,
}

//canvas is needed for engraving, texture creation
const canvas = document.createElement('canvas')
// TODO: To backend
const engraveConfig = {
  canvasWidth: 1000,
  canvasHeight: 950,
  fontSize: 65,
  color: 'rgba(0, 0, 0, 0.95)',
}

canvas.style.display = 'none'
canvas.width = engraveConfig.canvasWidth
canvas.height = engraveConfig.canvasHeight
const context = canvas.getContext('2d')

export const changeColor = (materialColor: MaterialColor, sku: string) => {
  const model = window.ExposedModelBySKU[sku]
  if (!model) return
  if (tweenMaterialsAnimations.has(materialColor.materialName)) {
    TWEEN.remove(
      tweenMaterialsAnimations.get(materialColor.materialName) as TWEEN.Tween<TColorOption>,
    )
    tweenMaterialsAnimations.delete(materialColor.materialName)
  }

  const { children } = model

  for (const group of children) {
    const materials: Array<Material> = []

    for (const mesh of group.children) {
      const material = (mesh as Mesh).material as Material
      if (material.name === materialColor.materialName) {
        materials.push(material)
      }
    }
    if (materials.length === 0) {
      continue
    }
    const current = {
      color: new Color(`#${materials[0].color.getHexString()}`),
    }
    const target = { color: new Color(materialColor.color) }
    const animationReference = animateChangeMaterials(materials, current, target)
    tweenMaterialsAnimations.set(materialColor.materialName, animationReference)
  }
  //set color via action
  window.ExposedStateBySKU[sku]?.setCurrentMaterialColor(materialColor)
}

export const setGemColor = (
  gemColor: MaterialColor,
  sku: string,
  gemSubName = '',
  uniformName = '_Color',
) => {
  const model = window.ExposedModelBySKU[sku]
  if (!model) return
  const { children } = model

  for (const group of children) {
    for (const mesh of group.children) {
      const material = (mesh as Mesh).material as ShaderMaterial
      if (mesh.name.indexOf(gemSubName) > -1) {
        const newColor = new Color(gemColor.color)
        // eslint-disable-next-line
        material.uniforms[uniformName].value.set(newColor.r, newColor.g, newColor.b)
      }
    }
  }
}

export const changeCut = (newCut: DiamondCut, sku: string) => {
  const model = window.ExposedModelBySKU[sku]
  const state = window.ExposedStateBySKU[sku]
  if (!model || !state || !state.viewerConfig) return
  const diamondCuts = state.viewerConfig.diamondCuts
  const { children } = model
  const currentCut = window.ExposedStateBySKU[sku]?.currentDiamondCut
  if (currentCut?.name === newCut.name || currentCut?.frame === newCut.frame) {
    return
  }

  if (tweenCutAnimations.tweenIn) TWEEN.remove(tweenCutAnimations.tweenIn)
  if (tweenCutAnimations.tweenOut) TWEEN.remove(tweenCutAnimations.tweenOut)
  for (const group of children) {
    const prevMeshes: Array<Object3D> = []
    const newMeshes: Array<Object3D> = []

    for (const mesh of group.children) {
      if (diamondCuts?.some((cut) => cut.name === mesh.name || cut.frame === mesh.name)) {
        mesh.visible = false
      }
      if (mesh.name === newCut.name || mesh.name === newCut.frame) {
        newMeshes.push(mesh)
      }
      if (mesh.name === currentCut?.name || mesh.name === currentCut?.frame) {
        prevMeshes.push(mesh)
      }
    }

    if (currentCut) {
      tweenCutAnimations.tweenOut = animateChangeMeshes(prevMeshes, 0, 'out')
    }
    tweenCutAnimations.tweenIn = animateChangeMeshes(
      newMeshes,
      currentCut ? ANIMATION_DURATION : 0,
      'in',
    )
  }
  //set color via action
  state.setCurrentDiamondCut(newCut)
}

export const engrave = (text: string, font = 'Playfair Display', sku: string) => {
  function createTexture(text: string, font: string) {
    if (!canvas || !context) return

    context.clearRect(0, 0, engraveConfig.canvasWidth, canvas.height)
    context.fillStyle = 'rgba(255, 255, 255, 0)'
    context.fillRect(0, 0, engraveConfig.canvasWidth, canvas.height)

    context.textAlign = 'center'
    context.fillStyle = engraveConfig.color
    context.font = `${engraveConfig.fontSize}px ${font}`
    context.fillText(
      text.toUpperCase(),
      engraveConfig.canvasWidth * 0.5,
      engraveConfig.canvasWidth * 0.5,
    )

    const texture = new CanvasTexture(canvas)
    texture.needsUpdate = true

    return texture
  }
  function attachToModel(mesh: Mesh, texture: CanvasTexture | undefined) {
    if (!texture) return
    ;(mesh.material as MeshStandardMaterial).needsUpdate = true
    ;(mesh.material as MeshStandardMaterial).opacity = 0.5
    ;(mesh.material as MeshStandardMaterial).map = texture

    texture.dispose()
  }

  const texture = createTexture(text, font)
  const model = window.ExposedModelBySKU[sku]
  if (!model) return
  const { children } = model

  for (const group of children) {
    for (const mesh of group.children) {
      if (mesh.name === '#grav') {
        attachToModel(mesh as Mesh, texture)
      }
    }
  }
}

//TODO: retrieve availableLetters from config, not from params
export const changeLetter = (newLetter: string, availableLetters: string[], sku: string) => {
  const model = window.ExposedModelBySKU[sku]
  if (!model) return
  const { children } = model
  const currentLetter = window.ExposedStateBySKU[sku]?.currentLetter
  if (currentLetter === newLetter) return

  if (tweenLettersAnimations.tweenIn) TWEEN.remove(tweenLettersAnimations.tweenIn)
  if (tweenLettersAnimations.tweenOut) TWEEN.remove(tweenLettersAnimations.tweenOut)
  for (const group of children) {
    const prevMeshes: Array<Object3D> = []
    const currentMeshes: Array<Object3D> = []

    for (const mesh of group.children) {
      availableLetters.forEach((letter) => {
        if (mesh.name.includes(`Diamond_${letter}`) || mesh.name.includes(`Metal_${letter}`)) {
          mesh.visible = false
          if (
            mesh.name.includes(`Diamond_${currentLetter}`) ||
            mesh.name.includes(`Metal_${currentLetter}`)
          ) {
            prevMeshes.push(mesh)
          }
          if (
            mesh.name.includes(`Diamond_${newLetter}`) ||
            mesh.name.includes(`Metal_${newLetter}`)
          ) {
            currentMeshes.push(mesh)
          }
        }
      })
    }

    if (currentLetter) {
      tweenLettersAnimations.tweenOut = animateChangeMeshes(prevMeshes, 0, 'out')
    }
    tweenLettersAnimations.tweenIn = animateChangeMeshes(
      currentMeshes,
      currentLetter ? ANIMATION_DURATION : 0,
      'in',
    )
  }
  window.ExposedStateBySKU[sku]?.setCurrentLetter(newLetter)
}

export const changeHalo = (newHalo: StoneHalo, sku: string) => {
  const model = window.ExposedModelBySKU[sku]
  if (!model) return
  const currentHalo = window.ExposedStateBySKU[sku]?.currentHalo
  if (currentHalo?.name === newHalo.name) return
  if (tweenHalosAnimations.tweenIn) TWEEN.remove(tweenHalosAnimations.tweenIn)
  if (tweenHalosAnimations.tweenOut) TWEEN.remove(tweenHalosAnimations.tweenOut)
  const { children } = model
  for (const group of children) {
    const prevMeshes: Array<Object3D> = []
    const newMeshes: Array<Object3D> = []

    for (const mesh of group.children) {
      //TODO: remove remove this and hide inactive meshes BEFORE rendering, consultation needed
      if (
        window.ExposedStateBySKU[sku]?.viewerConfig?.stoneHalos &&
        (window.ExposedStateBySKU[sku]?.viewerConfig?.stoneHalos as Array<StoneHalo>).some(
          (halo) => halo.name === mesh.name || halo.frame === mesh.name,
        )
      ) {
        mesh.visible = false
      }
      if (mesh.name === newHalo.name || mesh.name === newHalo.frame) {
        newMeshes.push(mesh)
      }
      if (mesh.name === currentHalo?.name || mesh.name === currentHalo?.frame) {
        prevMeshes.push(mesh)
      }
    }

    if (currentHalo) {
      tweenCutAnimations.tweenOut = animateChangeMeshes(prevMeshes, 0, 'out')
    }
    tweenCutAnimations.tweenIn = animateChangeMeshes(
      newMeshes,
      currentHalo ? ANIMATION_DURATION : 0,
      'in',
    )
  }
  window.ExposedStateBySKU[sku]?.setCurrentHalo(newHalo)
}

export const changeBand = (newBand: DiamondBand, sku: string) => {
  const model = window.ExposedModelBySKU[sku]
  if (!model) return
  const currentBand = window.ExposedStateBySKU[sku]?.currentBand
  if (currentBand?.name === newBand.name) return
  if (tweenBandAnimations.tweenIn) TWEEN.remove(tweenBandAnimations.tweenIn)
  if (tweenBandAnimations.tweenOut) TWEEN.remove(tweenBandAnimations.tweenOut)
  const { children } = model
  for (const group of children) {
    const prevMeshes: Array<Object3D> = []
    const newMeshes: Array<Object3D> = []

    for (const mesh of group.children) {
      //TODO: remove remove this and hide inactive meshes BEFORE rendering, consultation needed
      if (
        window.ExposedStateBySKU[sku]?.viewerConfig?.diamondBands &&
        (window.ExposedStateBySKU[sku]?.viewerConfig?.diamondBands as Array<DiamondBandDto>).some(
          (band) => band.name === mesh.name,
        )
      ) {
        mesh.visible = false
      }
      if (mesh.name === newBand.name) {
        newMeshes.push(mesh)
      }
      if (mesh.name === currentBand?.name) {
        prevMeshes.push(mesh)
      }
    }

    if (currentBand) {
      tweenCutAnimations.tweenOut = animateChangeMeshes(prevMeshes, 0, 'out')
    }
    tweenCutAnimations.tweenIn = animateChangeMeshes(
      newMeshes,
      currentBand ? ANIMATION_DURATION : 0,
      'in',
    )
  }
  window.ExposedStateBySKU[sku]?.setCurrentBand(newBand)
}
